import type { NaturalGasNarrativeInput, NaturalGasViewId } from './naturalGasConsumptionNarrative';
import { isVerifiedGasProvenance } from './gasProviderNormalization';
import {
  latestCommonPositiveYear,
  latestCompatiblePerCapitaPair,
  latestPositiveYear,
  percentChangeSinceBaseline,
  sectorTotalsByYear,
  sharesForYear,
  valueAtYear,
} from './energySectorCommonYear';

const THERMS_TO_GJ = 0.1055;

export type NaturalGasChartSeries = {
  chartData?: { labels?: number[]; datasets?: any[] };
  latestYear?: number;
  latestValue?: number;
};

export function buildNaturalGasNarrativeInputFromCharts(params: {
  locationName: string;
  viewId: NaturalGasViewId;
  baselineYear: number;
  gasRes: NaturalGasChartSeries;
  gasCom: NaturalGasChartSeries;
  gasPerCapitaData: NaturalGasChartSeries | null;
  californiaGasPerCapita: NaturalGasChartSeries | null;
  hasEstimatedYears: boolean;
  activeResidentialModel?: string;
  activeCommercialModel?: string;
  gasSourceRow?: {
    data_source?: string | null;
    measurement?: string | null;
    method_note?: string | null;
  } | null;
}): NaturalGasNarrativeInput | null {
  const { gasRes, gasCom, baselineYear } = params;
  if (!gasRes?.chartData || !gasCom?.chartData) return null;

  const provenanceFromRow = params.gasSourceRow;
  const verifiedUtility = provenanceFromRow
    ? isVerifiedGasProvenance({
        data_source: provenanceFromRow.data_source || '',
        measurement: provenanceFromRow.measurement || '',
      })
    : false;
  const provenanceStatus: NaturalGasNarrativeInput['provenanceStatus'] = verifiedUtility
    ? 'format_table_pipeline'
    : 'NEEDS_SOURCE_VERIFICATION';
  const sourceAgency = verifiedUtility && provenanceFromRow?.data_source
    ? provenanceFromRow.data_source
    : null;

  const resByYear = sectorTotalsByYear(gasRes.chartData);
  const comByYear = sectorTotalsByYear(gasCom.chartData);
  const commonYear = latestCommonPositiveYear(resByYear, comByYear);
  const compositionShares = commonYear != null ? sharesForYear(resByYear, comByYear, commonYear) : null;

  const resLatestYear = latestPositiveYear(resByYear);
  const comLatestYear = latestPositiveYear(comByYear);

  let latestYear: number | null = null;
  let totalTherms: number | null = null;
  let residentialTherms: number | null = null;
  let commercialTherms: number | null = null;
  let percentChange: number | null = null;
  let residentialShare: number | null = null;
  let commercialShare: number | null = null;

  if (params.viewId === 'total') {
    if (commonYear == null || !compositionShares) {
      return null;
    }
    latestYear = commonYear;
    totalTherms = compositionShares.total;
    residentialShare = compositionShares.residentialShare;
    commercialShare = compositionShares.commercialShare;
    const baseTotal =
      (valueAtYear(resByYear, baselineYear) ?? 0) + (valueAtYear(comByYear, baselineYear) ?? 0);
    const baseOk =
      valueAtYear(resByYear, baselineYear) != null && valueAtYear(comByYear, baselineYear) != null;
    percentChange = baseOk ? percentChangeSinceBaseline(totalTherms, baseTotal) : null;
  } else if (params.viewId === 'res') {
    if (resLatestYear == null) return null;
    latestYear = resLatestYear;
    residentialTherms = valueAtYear(resByYear, resLatestYear);
    residentialShare = compositionShares?.residentialShare ?? null;
    commercialShare = compositionShares?.commercialShare ?? null;
    const baseRes = valueAtYear(resByYear, baselineYear);
    percentChange =
      residentialTherms != null ? percentChangeSinceBaseline(residentialTherms, baseRes) : null;
  } else if (params.viewId === 'com') {
    if (comLatestYear == null) return null;
    latestYear = comLatestYear;
    commercialTherms = valueAtYear(comByYear, comLatestYear);
    residentialShare = compositionShares?.residentialShare ?? null;
    commercialShare = compositionShares?.commercialShare ?? null;
    const baseCom = valueAtYear(comByYear, baselineYear);
    percentChange =
      commercialTherms != null ? percentChangeSinceBaseline(commercialTherms, baseCom) : null;
  } else {
    // perCapita
    const pc = params.gasPerCapitaData?.chartData;
    if (!pc?.labels?.length) return null;
    const labels = pc.labels as number[];
    const data = (pc.datasets?.[0]?.data || []) as number[];
    const localLatestYear = params.gasPerCapitaData?.latestYear ?? labels[labels.length - 1];
    const iLoc = labels.indexOf(localLatestYear);
    const gjPerson =
      iLoc >= 0 ? Number(data[iLoc]) : Number(params.gasPerCapitaData?.latestValue);
    if (!Number.isFinite(gjPerson) || gjPerson <= 0) return null;
    latestYear = localLatestYear;
    const localThermsPerPerson = gjPerson / THERMS_TO_GJ;
    const i2005 = labels.indexOf(baselineYear);
    const baseGj = i2005 >= 0 ? Number(data[i2005]) : NaN;
    percentChange =
      baseGj > 0 ? percentChangeSinceBaseline(localThermsPerPerson, baseGj / THERMS_TO_GJ) : null;

    let comparisonYear: number | null = null;
    let californiaThermsPerPerson: number | null = null;
    let comparisonCompatibility: NaturalGasNarrativeInput['comparisonCompatibility'] = 'none';

    const ca = params.californiaGasPerCapita?.chartData;
    if (ca?.labels?.length && ca.datasets?.[0]?.data) {
      const pair = latestCompatiblePerCapitaPair(
        labels,
        data,
        ca.labels as number[],
        ca.datasets[0].data as number[]
      );
      if (pair) {
        comparisonYear = pair.year;
        californiaThermsPerPerson = pair.compare / THERMS_TO_GJ;
        comparisonCompatibility = 'compatible';
      }
    }

    return {
      locationName: params.locationName,
      viewId: params.viewId,
      latestYear,
      baselineYear,
      totalTherms: null,
      residentialTherms: null,
      commercialTherms: null,
      percentChange,
      residentialShare: null,
      commercialShare: null,
      localThermsPerPerson,
      californiaThermsPerPerson,
      comparisonYear,
      comparisonCompatibility,
      hasEstimatedYears: params.hasEstimatedYears,
      activeResidentialModel: params.activeResidentialModel,
      activeCommercialModel: params.activeCommercialModel,
      provenanceStatus,
      sourceAgency,
      sourceDataset: verifiedUtility ? 'DIRECT_UTILITY_DATA' : null,
    };
  }

  return {
    locationName: params.locationName,
    viewId: params.viewId,
    latestYear,
    baselineYear,
    totalTherms,
    residentialTherms,
    commercialTherms,
    percentChange,
    residentialShare,
    commercialShare,
    localThermsPerPerson: null,
    californiaThermsPerPerson: null,
    comparisonYear: null,
    comparisonCompatibility: 'none',
    hasEstimatedYears: params.hasEstimatedYears,
    activeResidentialModel: params.activeResidentialModel,
    activeCommercialModel: params.activeCommercialModel,
    provenanceStatus,
    sourceAgency,
    sourceDataset: verifiedUtility ? 'DIRECT_UTILITY_DATA' : null,
  };
}
